import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X } from 'lucide-react';
import { getNotifications, getUnreadCount, markNotificationsRead, markAllNotificationsRead } from '../api/chatAI';

const TYPE_STYLES = {
  stage_transition_handoff_in:  { color: '#3b82f6', prefix: 'Now responsible:' },
  stage_transition_handoff_out: { color: '#6b7280', prefix: 'Handed off:' },
  task_escalated:               { color: '#d94f4f', prefix: 'Escalated:' },
};

function relativeTime(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export default function NotificationBell() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const lastFetchRef = useRef(0);
  const dropdownRef = useRef(null);

  // Poll unread count every 60s
  useEffect(() => {
    const poll = () => getUnreadCount().then(setUnreadCount).catch(() => {});
    poll();
    const interval = setInterval(poll, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const list = await getNotifications({ limit: 50 });
      setNotifications(list);
      lastFetchRef.current = Date.now();
    } catch (e) { /* non-blocking */ }
    finally { setLoading(false); }
  }, []);

  const handleToggle = useCallback(() => {
    setOpen(prev => {
      const next = !prev;
      if (next) {
        // Refetch if stale (>30s) or first open
        if (Date.now() - lastFetchRef.current > 30000) fetchList();
      }
      return next;
    });
  }, [fetchList]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleMarkRead = async (id) => {
    try {
      await markNotificationsRead([id]);
      setUnreadCount(prev => Math.max(0, prev - 1));
      // Update local state
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
    } catch (e) { /* non-blocking */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
    } catch (e) { /* non-blocking */ }
  };

  const handleRowClick = (n) => {
    if (n.record_id) {
      window.dispatchEvent(new CustomEvent('panelNavigate', { detail: { record_id: n.record_id } }));
      setOpen(false);
    }
  };

  const style = TYPE_STYLES[notifications[0]?.type] || {};

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={handleToggle}
        className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
        title="Notifications"
      >
        <Bell className="h-4 w-4" style={{ color: '#c9a84c' }} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-bold text-white"
            style={{ background: '#c9a84c' }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            width: 360,
            maxHeight: 480,
            background: '#111215',
            border: '1px solid #2a2a35',
            borderRadius: 12,
            zIndex: 50,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          }}
        >
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 14px', borderBottom: '1px solid #2a2a35',
          }}>
            <span style={{ color: '#f5f0e8', fontSize: 13, fontWeight: 600 }}>Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={() => handleMarkAllRead()}
                style={{ background: 'none', border: 'none', color: '#c9a84c', fontSize: 11, cursor: 'pointer', fontWeight: 500 }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading && notifications.length === 0 && (
              <div style={{ padding: 24, textAlign: 'center', color: '#6b7280', fontSize: 12 }}>Loading...</div>
            )}
            {!loading && notifications.length === 0 && (
              <div style={{ padding: 24, textAlign: 'center', color: '#6b7280', fontSize: 12 }}>No notifications yet.</div>
            )}
            {notifications.map(n => {
              const isUnread = !n.read_at;
              const ts = TYPE_STYLES[n.type] || { color: '#c9a84c', prefix: '' };
              return (
                <div
                  key={n.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '10px 14px',
                    borderLeft: isUnread ? '2px solid #c9a84c' : '2px solid transparent',
                    cursor: n.record_id ? 'pointer' : 'default',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#1a1b20'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  {/* Type icon dot */}
                  <div style={{
                    width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                    background: ts.color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: ts.color }} />
                  </div>

                  {/* Body — clickable */}
                  <div style={{ flex: 1, minWidth: 0 }} onClick={() => handleRowClick(n)}>
                    <div style={{
                      fontSize: 12, color: '#c9a84c', fontWeight: isUnread ? 600 : 400,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {ts.prefix ? `${ts.prefix} ` : ''}{n.title}
                    </div>
                    {n.body && (
                      <div style={{
                        fontSize: 11, color: '#9ca3af', marginTop: 2,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {n.body}
                      </div>
                    )}
                  </div>

                  {/* Timestamp + dismiss */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                    <span style={{ fontSize: 10, color: '#6b7280', whiteSpace: 'nowrap' }}>{relativeTime(n.created_at)}</span>
                    {isUnread && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleMarkRead(n.id); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1 }}
                        title="Mark as read"
                      >
                        <X className="h-3 w-3" style={{ color: '#6b7280' }} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
